import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanySelfDevelopmentUsers } from './companySelfDevelopmentApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the Self Development section.
 *
 * One column per tool plus a "Tools" column, because the two say different
 * things: forty entries all in one tool is a person with one habit, and forty
 * spread over four is a person with a practice. Sorting is by total entries,
 * then by breadth, so the second is the tie-break.
 *
 * Every user of the population is listed, including those who have never opened
 * the section — they show dashes rather than zeros, so "hasn't used it" reads
 * differently from "used it once and stopped".
 *
 * Counts only. No row here can say what anybody wrote.
 */
const SelfDevelopmentUsersTable = ({
  fetcher = fetchCompanySelfDevelopmentUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const active = row.totalEntries > 0;
          const cell = (value) => (active ? Number(value || 0).toLocaleString() : '-');
          return {
            ...row,
            totalText: cell(row.totalEntries),
            toolsText: active ? `${row.toolsUsed} of 6` : '-',
            gratitudeText: cell(row.gratitude),
            cleanseText: cell(row.cleanse),
            journalText: cell(row.journal),
            goalsText: active ? `${row.goals || 0} / ${row.goalsAchieved || 0}` : '-',
            boardsText: cell(row.visionBoards),
            affirmationsText: cell(row.affirmations)
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('SelfDevelopmentUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Entries', key: 'totalText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Tools', key: 'toolsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Gratitude', key: 'gratitudeText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Cleanse', key: 'cleanseText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Journal', key: 'journalText', sortable: false, align: 'center', type: 'text3' },
    // Set / achieved in one cell: a goal count on its own says nothing.
    { title: 'Goals (set/won)', key: 'goalsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Boards', key: 'boardsText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Affirmations',
      key: 'affirmationsText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

SelfDevelopmentUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default SelfDevelopmentUsersTable;
